import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Dialog, DialogTitle, IconButton, Typography, useMediaQuery, type SxProps, type Theme } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import BackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';

/**
 * The employee app's layout primitives.
 *
 * The office screens are wide tables and a tile grid; these three exist because
 * a phone in a gloved hand needs something different, and because repeating the
 * same `sx` block across four files is how the legacy views ended up with the
 * same 310px button width hard-coded in seven places.
 *
 * Three rules, applied everywhere in /my:
 *
 *   * ONE COLUMN, capped at 520px. Wider than that and a tablet stretches a
 *     form designed for a thumb across a whole screen.
 *   * TOUCH TARGETS of at least 52px tall. The legacy buttons were Bootstrap's
 *     default 38px, which is below every platform's minimum and is why people
 *     mis-tap Stop on a bumpy site.
 *   * SAFE AREAS. `env(safe-area-inset-bottom)` keeps the last button clear of
 *     the iPhone home indicator. index.html already sets `viewport-fit=cover`,
 *     which is what makes the inset non-zero.
 */

interface EmployeeShellProps {
    readonly title: string;
    readonly subtitle?: string | undefined;
    /** Where the back arrow goes. Omit it on the home screen, which has nothing to go back to. */
    readonly backTo?: string | undefined;
    readonly action?: ReactNode | undefined;
    /** Optional: an empty shell is what renders before the first response, in place of a spinner. */
    readonly children?: ReactNode | undefined;
}

export function EmployeeShell({
    title,
    subtitle,
    backTo,
    action,
    children,
}: EmployeeShellProps): React.JSX.Element {
    const navigate = useNavigate();

    return (
        <Box
            sx={{
                maxWidth: 520,
                mx: 'auto',
                px: 2,
                pt: 2,
                pb: 'calc(24px + env(safe-area-inset-bottom))',
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                {backTo !== undefined ? (
                    <IconButton
                        aria-label="Back"
                        onClick={() => {
                            void navigate(backTo);
                        }}
                        sx={{ ml: -1 }}
                    >
                        <BackIcon />
                    </IconButton>
                ) : null}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="h5" fontWeight={800} noWrap>
                        {title}
                    </Typography>
                    {subtitle !== undefined ? (
                        <Typography variant="body2" color="text.secondary" noWrap>
                            {subtitle}
                        </Typography>
                    ) : null}
                </Box>
                {action}
            </Box>
            {children}
        </Box>
    );
}

interface FullWidthButtonProps {
    readonly colour: string;
    readonly startIcon?: ReactNode;
    readonly disabled?: boolean;
    readonly onClick?: () => void;
    readonly sx?: SxProps<Theme>;
    readonly children: ReactNode;
}

/**
 * The employee app's action button: full width, 52px tall, tinted with its own
 * accent. Replaces the legacy `class="btn btn-primary" style="width: 310px"`.
 */
export function FullWidthButton({
    colour,
    startIcon,
    disabled = false,
    onClick,
    sx,
    children,
}: FullWidthButtonProps): React.JSX.Element {
    return (
        <Button
            fullWidth
            disableElevation
            startIcon={startIcon}
            disabled={disabled}
            onClick={onClick}
            sx={{
                minHeight: 52,
                borderRadius: 2,
                fontWeight: 700,
                fontSize: '1rem',
                textTransform: 'none',
                color: colour,
                border: `1.5px solid ${alpha(colour, 0.5)}`,
                bgcolor: alpha(colour, 0.12),
                '&:hover': { bgcolor: alpha(colour, 0.2), borderColor: colour },
                '&.Mui-disabled': { color: alpha(colour, 0.4), borderColor: alpha(colour, 0.2) },
                ...sx,
            }}
        >
            {children}
        </Button>
    );
}

interface SheetDialogProps {
    readonly open: boolean;
    readonly title: string;
    readonly onClose: () => void;
    readonly children: ReactNode;
}

/**
 * A dialog that is a full-screen sheet on a phone and a centred card above it.
 *
 * The legacy app navigated to a whole separate page for the stop-and-note step
 * (`employee-dashboard-add-note`), losing the shift context and costing a page
 * load. A sheet keeps the person where they were.
 */
export function SheetDialog({ open, title, onClose, children }: SheetDialogProps): React.JSX.Element {
    const theme = useTheme();
    const phone = useMediaQuery(theme.breakpoints.down('sm'));

    return (
        <Dialog open={open} onClose={onClose} fullScreen={phone} fullWidth maxWidth="xs">
            <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ flex: 1 }}>{title}</Box>
                <IconButton aria-label="Close" onClick={onClose} sx={{ mr: -1 }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            {children}
        </Dialog>
    );
}
